/**
 * The relay, as the app reaches it (part 36a, principles C4, C6): JSON requests to the configured origin only, each
 * answer read by its reader at the boundary. A refusal is the relay's one word (part 28's error bodies) with the wait a
 * 429 names; anything else that isn't the answer expected (a network failure, a time-out, an unreadable body, a word the
 * app doesn't know) is a failure. Never rejects. `fetch` and the clock's wait are injected, so tests run it against the
 * relay in the same process.
 */
import { fieldsOf } from './wire.ts';

/** Every word the relay refuses with (the relay's `ERROR_STATUS`). */
export const RELAY_ERRORS = [
  'not-found',
  'rate-limited',
  'timeout',
  'unauthenticated',
  'bad-length',
  'too-large',
  'internal',
  'bad-request',
  'login-failed',
  'login-throttled',
  'bad-challenge',
  'invalid-link',
  'device-revoked',
  'device-expired',
  'too-many-devices',
  'second-factor-on',
  'second-factor-failed',
  'ticket-invalid',
  'passkey-unavailable',
  'grant-invalid',
  'login-required',
  // Fix-next 84, LX9: a refresh of a device that logged itself out.
  'logged-out',
  'second-factor-off',
  'terms-outdated',
  'merchant-unavailable',
  'account-deleted',
  // Part 36b's: the operator page's sign-in while the relay names no operator origin (the operator page reads it).
  'ops-unavailable',
  // Part 38: the share links' words (the writer's side is part 38d).
  'link-ended',
  'link-unavailable',
  'purchase-required',
  'link-exists',
  'too-many-links',
  'over-quota',
  'link-full',
  'comments-off',
  'item-exists',
  // Part 38d's deep-fix r8: an owner's route for a link the relay doesn't hold, apart from a path it doesn't know.
  'link-gone',
  // Part 38a: the invites' words (the writer's side is part 38b, the invitee's 38c).
  'share-gone',
  'invite-gone',
  'invite-ended',
  'invite-claimed',
  'invite-address',
  'share-ended',
  'share-full',
  'copy-stale',
  'keyring-required',
  'change-pending',
  'keyring-exists',
  'share-exists',
  'invite-exists',
  'notes-off',
  'copy-unavailable',
] as const;

/** One of {@link RELAY_ERRORS}. */
export type RelayError = (typeof RELAY_ERRORS)[number];

/** An answer: the value read, a refusal (with the seconds to wait, if said), or a failure. */
export type Reply<T> =
  | { readonly kind: 'ok'; readonly value: T }
  | { readonly kind: 'refused'; readonly error: RelayError; readonly retryAfter: number | null }
  | { readonly kind: 'failed' };

/** The failure every unreadable answer is. */
export const FAILED: Reply<never> = Object.freeze({ kind: 'failed' });

/** A request as the client makes it (the browser's `fetch`, as far as used). */
export type Fetch = (url: string, init: { readonly method: string; readonly headers: Record<string, string>; readonly body: string | Uint8Array<ArrayBuffer> | null; readonly signal: AbortSignal }) => Promise<Response>;

/** How long a request may take before it is given up as failed. */
export const REQUEST_TIMEOUT_MS = 20_000;

/**
 * Table SB (part 38d's slow uplink): how long a snapshot part's upload ({@link RelayClient.postBytes}) may take: the
 * relay's 10 s deadline and its 120 s for a slow body, and 10 s for the answer to come back.
 */
export const UPLOAD_TIMEOUT_MS = 140_000;

/** The relay as the app talks to it. */
export interface RelayClient {
  /** POSTs `body` as JSON to `path`, with `token` as the session if given; the answer read by `read`. */
  post<T>(path: string, body: unknown, read: (value: unknown) => T | null, token?: string): Promise<Reply<T>>;
  /** GETs `path` with the session `token`; the answer read by `read`. */
  get<T>(path: string, read: (value: unknown) => T | null, token: string): Promise<Reply<T>>;
  /** Part 38d: POSTs `bytes` as they are (a snapshot part's frame) to `path` with the session `token`; the answer read by `read`. */
  postBytes<T>(path: string, bytes: Uint8Array<ArrayBuffer>, read: (value: unknown) => T | null, token: string): Promise<Reply<T>>;
  /**
   * Part 39: POSTs `body` as JSON to `path` with no session, for an answer of bytes (a snapshot part): a 2xx's body of at
   * most `most` bytes, else a failure; a refusal as {@link post} reads one. It may take a part's time (part 39's DL2:
   * {@link UPLOAD_TIMEOUT_MS}, as an upload's), not a JSON answer's. Part 38c: with the session `token` when given (an
   * invitee's copy).
   */
  postForBytes(path: string, body: unknown, most: number, token?: string): Promise<Reply<Uint8Array<ArrayBuffer>>>;
}

/** What the client runs on. */
export interface ClientOptions {
  /** The relay's origin (`config.ts`): every request goes there and nowhere else. */
  readonly origin: string;
  readonly fetch: Fetch;
  /** Resolves after `ms` (the time-out's clock). */
  readonly delay: (ms: number) => Promise<void>;
}

/** Seconds from a `Retry-After` header: a whole number of zero or more, or null. */
export function retryAfterOf(header: string | null): number | null {
  return header !== null && /^[0-9]{1,9}$/u.test(header) ? Number(header) : null;
}

/** C4: an answer read: a 2xx whose body `read` takes, a refusal with a known word, or a failure. */
async function replyOf<T>(response: Response, read: (value: unknown) => T | null): Promise<Reply<T>> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return FAILED;
  }
  if (response.status >= 200 && response.status < 300) {
    const value = read(body);
    return value === null ? FAILED : { kind: 'ok', value };
  }
  return refusalOf(response, body);
}

/** Part 39: a 2xx's bytes, at most `most` of them; anything else as {@link replyOf} reads a refusal. */
async function bytesOf(response: Response, most: number): Promise<Reply<Uint8Array<ArrayBuffer>>> {
  if (response.status < 200 || response.status >= 300) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      return FAILED;
    }
    return refusalOf(response, body);
  }
  const bytes = new Uint8Array(await response.arrayBuffer());
  return bytes.length > most ? FAILED : { kind: 'ok', value: bytes };
}

/** A refusal's body read: the relay's word and the wait a 429 names, or a failure for a word the app doesn't know. */
function refusalOf(response: Response, body: unknown): Reply<never> {
  const word = fieldsOf(body)?.['error'];
  const error = RELAY_ERRORS.find((known) => known === word);
  return error === undefined ? FAILED : { kind: 'refused', error, retryAfter: retryAfterOf(response.headers.get('retry-after')) };
}

/** The client over `options`. */
export function relayClient(options: ClientOptions): RelayClient {
  const send = async <T>(
    method: string,
    path: string,
    body: unknown,
    answer: (response: Response) => Promise<Reply<T>>,
    token: string | undefined,
    timeout = REQUEST_TIMEOUT_MS,
  ): Promise<Reply<T>> => {
    const headers: Record<string, string> = {};
    const raw = body instanceof Uint8Array;
    if (body !== undefined) headers['content-type'] = raw ? 'application/octet-stream' : 'application/json';
    if (token !== undefined) headers['authorization'] = `Bearer ${token}`;
    const abort = new AbortController();
    const timedOut = options.delay(timeout).then(() => {
      abort.abort();
      return FAILED;
    });
    const answered = (async (): Promise<Reply<T>> => {
      try {
        const sent = raw ? new Uint8Array(body) : body === undefined ? null : JSON.stringify(body);
        const response = await options.fetch(`${options.origin}${path}`, { method, headers, body: sent, signal: abort.signal });
        return await answer(response);
      } catch {
        return FAILED;
      }
    })();
    return Promise.race([answered, timedOut]);
  };
  return {
    post: (path, body, read, token) => send('POST', path, body, (response) => replyOf(response, read), token),
    get: (path, read, token) => send('GET', path, undefined, (response) => replyOf(response, read), token),
    postBytes: (path, bytes, read, token) => send('POST', path, bytes, (response) => replyOf(response, read), token, UPLOAD_TIMEOUT_MS),
    postForBytes: (path, body, most, token) => send('POST', path, body, (response) => bytesOf(response, most), token, UPLOAD_TIMEOUT_MS),
  };
}
